import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { showreel } from '../data/showreel';
import { INTRO, reelRise } from '../utils/reveal';

/** Final size of the reel once it has shrunk (reference: ~64% of the viewport width) */
const END_SCALE = 0.64;
/** Corner radius of the shrunk reel, in on-screen px */
const END_RADIUS = 16;
/** Phones: final height of the full-width reel, as a share of the screen height */
const MOBILE_END_HEIGHT = 0.42;

/**
 * Showreel, TinyWins-style:
 *  1. scrolling in, the video drifts slower than the page inside its frame (parallax)
 *  2. once it fills the screen it pins, and further scrolling shrinks it into a rounded card
 *  3. then it scrolls away with the page
 */
export function ShowreelVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();

  // 1 — entering: section top travels from the viewport bottom to the top
  const {
    scrollYProgress: enter
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'start start']
  });
  // 2 — pinned: from the section top hitting the top until its end reaches the bottom
  const {
    scrollYProgress: pinned
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end']
  });
  const enterY = useTransform(enter, [0, 1], ['-11%', '0%']);
  const pinnedY = useTransform(pinned, [0, 1], ['0%', '5%']);
  const videoY = useTransform([enterY, pinnedY], ([a, b]) => `calc(${a as string} + ${b as string})`);

  // Shrink over most of the pinned stretch, then hold briefly before it scrolls off
  const scale = useTransform(pinned, [0, 0.85], [1, END_SCALE]);
  // Counter the scale so corners land at a true END_RADIUS px on screen
  const radius = useTransform(scale, s => `${END_RADIUS * (1 - (s - END_SCALE) / (1 - END_SCALE)) / s}px`);

  // Phones: keeps the full screen width and only shrinks in height (cropped top and bottom, not squashed),
  // down to MOBILE_END_HEIGHT of the screen — a stronger squeeze than the desktop card
  const [mobile, setMobile] = React.useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const clipPath = useTransform(pinned, [0, 0.85], [0, (1 - MOBILE_END_HEIGHT) / 2], {
    clamp: true
  });
  const mobileClip = useTransform(clipPath, c => `inset(${(c * 100).toFixed(2)}% 0% ${(c * 100).toFixed(2)}% 0%)`);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.pause();
      return;
    }
    // Only play while on screen
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => undefined);else video.pause();
    }, {
      threshold: 0.1
    });
    io.observe(video);
    return () => io.disconnect();
  }, []);
  return (
    // Tall track: the extra height is the scroll distance the reel stays pinned while shrinking
    // Transparent: only the video card itself covers Pixel, so he shows again around it as it shrinks
    <section ref={sectionRef} aria-label="Agency showreel" className={`pointer-events-none relative z-[41] w-full ${reduce ? 'h-screen' : 'h-[220vh]'}`} data-id="element-201" data-mp="3ce50ok2tbmhm">
      <div className="sticky top-0 h-screen w-full overflow-hidden" data-id="element-202" data-mp="3halmh7mjhorj">
        {/* Page-load: eases up from the bottom edge */}
        <motion.div className="absolute inset-0" {...reelRise(INTRO.reel, reduce)} data-id="element-203" data-mp="3r2hupbicav9h">
          <motion.div className="absolute inset-0 overflow-hidden bg-black will-change-transform" style={reduce ? undefined : mobile ? {
            clipPath: mobileClip
          } : {
            scale,
            borderRadius: radius
          }} data-id="element-204" data-mp="3r4c69z7q1b0w">
            {/* Taller than the frame so it can glide inside without showing edges */}
            <motion.video ref={videoRef} style={{
              y: reduce ? 0 : videoY
            }} src={showreel.videoSrc} muted loop playsInline autoPlay preload="auto" disablePictureInPicture disableRemotePlayback tabIndex={-1} aria-hidden="true" className="pointer-events-none absolute inset-x-0 -top-[12%] h-[124%] w-full object-cover will-change-transform" data-id="element-205" data-mp="wnto833z7049" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}